// docs/109 — how the small peoples stand on the board. A foe's Oryx frame says what it is; some of
// them are not flesh and should not look it: ghosts are half there and hover, fire spirits flicker
// and shed embers, the frost folk drop flakes, a vampire trails red mist, a djinn curls smoke, the
// eyes hover in a violet pulse, the fey glitter, a mimic's lid glints like gold, and the duergar are
// grey as the stone they come from. Every part lives inside the unit's container (so it walks and
// sorts with it); reduce motion keeps the glows and the greying and drops the particles and the bob.
import Phaser from 'phaser';
import { TEX, ensureSpellTextures } from '../spells/textures';
import type { AuraQuality } from '../status/StatusAura';

type EmitCfg = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;
type Zone = Phaser.Types.GameObjects.Particles.RandomZoneSource;
const ADD = Phaser.BlendModes.ADD;

/** One people's standing look. */
export interface KinLook {
  /** The body's most opacity (a ghost is half there). */
  alpha?: number;
  /** Pixels it bobs up and down, hovering. */
  hover?: number;
  /** A glow under it: colour, alpha, and how much it flickers (0 steady … 1 a candle). */
  glow?: [number, number, number];
  /** A fill-tinted copy over the body at this alpha (the duergar's grey). */
  veil?: [number, number];
  /** What drifts off it. */
  motes?: 'embers' | 'flakes' | 'wisps' | 'mist' | 'smoke' | 'sparkle' | 'dust' | 'leaves' | 'glint';
  /** The motes' colour. */
  tint?: number;
}

const SPECTRAL: KinLook = { alpha: 0.72, hover: 1.5, glow: [0x9fd8ff, 0.3, 0.15], motes: 'wisps', tint: 0xcfe8ff };
const FIRE: KinLook = { glow: [0xff8a2a, 0.45, 0.8], motes: 'embers', tint: 0xffb347 };
const COLD: KinLook = { glow: [0x8fd4ff, 0.35, 0.3], motes: 'flakes', tint: 0xe6f6ff };
const EYE: KinLook = { hover: 2, glow: [0xb07cff, 0.35, 0.5] };

/** The looks by Oryx creature frame A (fk-sim `Kin` rows, docs/109). */
export const KIN_LOOKS: ReadonlyMap<number, KinLook> = new Map<number, KinLook>([
  // ghosts, specters and the shade
  [294, { ...SPECTRAL, glow: [0x6a5a9a, 0.35, 0.2], tint: 0x8a7ab8 }], [295, SPECTRAL],
  [371, { ...SPECTRAL, glow: [0xff6a6a, 0.3, 0.2], tint: 0xffb0b0 }], [372, SPECTRAL],
  [373, { ...SPECTRAL, glow: [0xd2a86a, 0.3, 0.2], tint: 0xe8d0a8 }],
  // wisps
  [361, { alpha: 0.9, hover: 2, glow: [0x7ff6ff, 0.5, 0.6], motes: 'sparkle', tint: 0x7ff6ff }],
  [362, { alpha: 0.9, hover: 2, glow: [0xff7fd8, 0.5, 0.6], motes: 'sparkle', tint: 0xff9fe0 }],
  // fire: the fire minion, the flame, the fire demon, the lava golem
  [365, FIRE], [377, { ...FIRE, alpha: 0.92 }], [189, FIRE], [194, { ...FIRE, glow: [0xff5a1a, 0.4, 0.5] }],
  // the other demons and the imp: a few sparks, a smoulder at the feet
  [188, { glow: [0xff3a2a, 0.25, 0.4], motes: 'embers', tint: 0xff6a3a }],
  [190, { glow: [0xff3a2a, 0.25, 0.4], motes: 'embers', tint: 0xff6a3a }],
  [342, { glow: [0xff3a2a, 0.2, 0.4], motes: 'embers', tint: 0xff6a3a }],
  // cold: the cold flame, the ice minion, the ice elemental, the frost witch, the white yetis
  [378, { ...COLD, alpha: 0.92 }], [366, COLD], [269, COLD], [305, COLD],
  [329, { motes: 'flakes', tint: 0xffffff }], [330, { motes: 'flakes', tint: 0xffffff }],
  // the vampires: red mist at their feet
  [301, { motes: 'mist', tint: 0x8a1020 }], [302, { motes: 'mist', tint: 0x8a1020 }], [303, { glow: [0xaa1030, 0.2, 0.2], motes: 'mist', tint: 0x8a1020 }],
  // smoke: the smoke minion and the djinn
  [367, { alpha: 0.85, hover: 1, motes: 'smoke', tint: 0x9a9aa8 }],
  [196, { hover: 1.5, glow: [0x7fb2ff, 0.35, 0.3], motes: 'smoke', tint: 0xa8c8ff }],
  // earth: the earth elemental, stone and mud golems, the mud minion
  [268, { motes: 'dust', tint: 0xa88a62 }], [191, { motes: 'dust', tint: 0x9a9a9a }], [192, { motes: 'dust', tint: 0x8a6a42 }], [368, { motes: 'dust', tint: 0x8a6a42 }],
  // the air elemental
  [270, { alpha: 0.88, hover: 1.5, motes: 'leaves', tint: 0xdfe8f0 }],
  // the beholder and its eyes
  [221, EYE], [369, EYE], [370, EYE],
  // the fey: pixie, thorn-queen, treant guard, mandrakes
  [341, { hover: 1.5, glow: [0xfff3a0, 0.3, 0.5], motes: 'sparkle', tint: 0xfff3a0 }],
  [152, { motes: 'sparkle', tint: 0xb8ff9a }], [197, { motes: 'leaves', tint: 0xa8d060 }],
  [363, { motes: 'sparkle', tint: 0xd8ffa8 }], [364, { motes: 'sparkle', tint: 0xb0c870 }],
  // the mimic's false gold
  [198, { motes: 'glint', tint: 0xffe070 }],
  // the duergar, grey as their stone
  [111, { veil: [0x7c838c, 0.45] }], [112, { veil: [0x7c838c, 0.45] }], [113, { veil: [0x7c838c, 0.45] }],
]);

/** The look for a unit with this frame, if any (enemies only: a hero's frame never is one). */
export const kinLookOf = (appearance: number | undefined, enemy: boolean): KinLook | undefined =>
  enemy && appearance !== undefined ? KIN_LOOKS.get(appearance) : undefined;

/** What the look needs of its unit (UnitSprite). */
export interface KinHost {
  readonly scene: Phaser.Scene;
  readonly container: Phaser.GameObjects.Container;
  readonly body: Phaser.GameObjects.Image;
  readonly bodies: () => Phaser.GameObjects.Image[];
  readonly cell: number;
  readonly fallen: () => boolean;
}

export class KinAura {
  private parts: Phaser.GameObjects.GameObject[] = [];
  private glow?: Phaser.GameObjects.Image;
  private veil?: Phaser.GameObjects.Image;
  private bob = 0;
  private readonly phase = Math.random() * Math.PI * 2;
  private readonly onUpdate = (t: number) => this.update(t);
  private dead = false;

  constructor(private readonly host: KinHost, readonly look: KinLook, private readonly q: AuraQuality) {
    const { scene, container, body, cell } = host;
    ensureSpellTextures(scene);
    if (look.glow) {
      const [colour, a] = look.glow;
      this.glow = scene.add.image(0, -2, TEX.glow).setTint(colour).setBlendMode(ADD).setAlpha(a).setScale((cell * 1.1) / 32, (cell * 0.55) / 32);
      container.addAt(this.glow, 1);
      this.parts.push(this.glow);
    }
    if (look.veil) {
      this.veil = scene.add.image(0, 0, body.texture.key, body.frame.name).setOrigin(body.originX, body.originY).setTint(look.veil[0]).setTintMode(Phaser.TintModes.FILL);
      container.addAt(this.veil, container.getIndex(body) + 1);
      this.parts.push(this.veil);
    }
    const e = look.motes && this.motes(look.motes, look.tint ?? 0xffffff);
    if (e) this.parts.push(e);
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    container.once(Phaser.GameObjects.Events.DESTROY, () => this.destroy());
  }

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    this.host.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
  }

  private update(t: number): void {
    if (this.dead || !this.host.container.active) return;
    const { look, host } = this;
    if (host.fallen()) {
      // a fallen ghost or flame is gone: its glow and motes with it
      for (const p of this.parts) (p as unknown as Phaser.GameObjects.Components.Visible).setVisible(false);
      return;
    }
    const s = t / 1000 + this.phase;
    if (look.alpha !== undefined) for (const b of host.bodies()) if (b.alpha > look.alpha) b.setAlpha(look.alpha);
    if (this.glow && look.glow) {
      const [, a, flicker] = look.glow;
      const f = this.q.still ? 1 : 1 - flicker * 0.5 * (0.5 + 0.5 * Math.sin(s * 9) * Math.sin(s * 3.7));
      this.glow.setAlpha(a * f);
    }
    if (this.veil && look.veil) {
      const b = host.body;
      if (this.veil.texture.key !== b.texture.key || this.veil.frame.name !== b.frame.name) this.veil.setTexture(b.texture.key, b.frame.name);
      this.veil.setPosition(b.x, b.y).setFlipX(b.flipX).setAngle(b.angle).setScale(b.scaleX, b.scaleY).setAlpha(look.veil[1] * b.alpha).setVisible(b.visible);
    }
    if (look.hover && !this.q.still) {
      // move the bodies by the change in the bob, so a playback's own offsets are left alone
      const bob = Math.round(-look.hover * (0.5 + 0.5 * Math.sin(s * 2.2)));
      const d = bob - this.bob;
      if (d) for (const b of host.bodies()) b.y += d;
      this.bob = bob;
    }
  }

  /** A looping emitter inside the container, thinned by the effect intensity; none with reduce motion. */
  private motes(kind: NonNullable<KinLook['motes']>, tint: number): Phaser.GameObjects.Particles.ParticleEmitter | undefined {
    if (this.q.still || this.q.density < 0.15) return undefined;
    const { cell } = this.host;
    const h = Math.max(this.host.body.height, cell * 0.7);
    const w = cell * 0.5;
    const zone = (x: number, y: number, zw: number, zh: number) => ({ type: 'random' as const, source: new Phaser.Geom.Rectangle(x, y, zw, zh) as unknown as Zone });
    const cfgs: Record<NonNullable<KinLook['motes']>, [string, EmitCfg]> = {
      embers: [TEX.dot, { frequency: 260, lifespan: 900, speedY: { min: -26, max: -12 }, speedX: { min: -6, max: 6 }, scale: { start: 0.5, end: 0 }, alpha: { start: 1, end: 0 }, blendMode: ADD, emitZone: zone(-w / 2, -h * 0.8, w, h * 0.6) }],
      flakes: [TEX.flake, { frequency: 420, lifespan: 1400, speedY: { min: 6, max: 14 }, speedX: { min: -5, max: 5 }, scale: { start: 0.45, end: 0.2 }, alpha: { start: 0.9, end: 0 }, emitZone: zone(-w, -h - 4, w * 2, 6) }],
      wisps: [TEX.smoke, { frequency: 520, lifespan: 1600, speedY: { min: -12, max: -6 }, speedX: { min: -3, max: 3 }, scale: { start: 0.25, end: 0.6 }, alpha: { start: 0.35, end: 0 }, blendMode: ADD, emitZone: zone(-w / 2, -h * 0.6, w, h * 0.5) }],
      mist: [TEX.smoke, { frequency: 380, lifespan: 1500, speedX: { min: -8, max: 8 }, speedY: { min: -2, max: 1 }, scale: { start: 0.3, end: 0.7 }, alpha: { start: 0.4, end: 0 }, emitZone: zone(-w, -4, w * 2, 4) }],
      smoke: [TEX.smoke, { frequency: 340, lifespan: 1500, speedY: { min: -14, max: -6 }, speedX: { min: -5, max: 5 }, rotate: { min: 0, max: 360 }, scale: { start: 0.3, end: 0.75 }, alpha: { start: 0.45, end: 0 }, emitZone: zone(-w / 2, -h * 0.3, w, h * 0.3) }],
      sparkle: [TEX.star, { frequency: 480, lifespan: 800, speed: { min: 2, max: 8 }, scale: { start: 0.5, end: 0 }, alpha: { start: 1, end: 0 }, blendMode: ADD, emitZone: zone(-w * 0.8, -h, w * 1.6, h) }],
      dust: [TEX.pebble, { frequency: 900, lifespan: 700, speedY: { min: 8, max: 20 }, speedX: { min: -8, max: 8 }, scale: { start: 0.35, end: 0.2 }, alpha: { start: 0.8, end: 0 }, emitZone: zone(-w / 2, -h * 0.5, w, h * 0.4) }],
      leaves: [TEX.leaf, { frequency: 700, lifespan: 1300, speedX: { min: -16, max: 16 }, speedY: { min: -6, max: 8 }, rotate: { min: 0, max: 360 }, scale: { start: 0.4, end: 0.2 }, alpha: { start: 0.9, end: 0 }, emitZone: zone(-w, -h, w * 2, h) }],
      glint: [TEX.star, { frequency: 1800, lifespan: 500, speed: 0, scale: { start: 0.7, end: 0 }, alpha: { start: 1, end: 0 }, blendMode: ADD, emitZone: zone(-w * 0.6, -h * 0.7, w * 1.2, h * 0.3) }],
    };
    const [tex, cfg] = cfgs[kind];
    const f = typeof cfg.frequency === 'number' ? cfg.frequency / Math.max(0.3, this.q.density) : cfg.frequency;
    const e = this.host.scene.add.particles(0, 0, tex, { ...cfg, tint, frequency: f });
    this.host.container.add(e);
    return e;
  }
}
